import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { uploadPublic, fileUrl } from "@/lib/upload";
import DateInput from "@/components/DateInput";
import { ShieldCheck, Loader2, UploadCloud, FileCheck2, X } from "lucide-react";

export const CASTES = ["OC", "BC-A", "BC-B", "BC-C", "BC-D", "BC-E", "SC", "ST", "Others"];

export function PubField({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function StudentStep({ form, upd, classes }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2"><PubField label="Student Full Name *"><input className="pub-input" value={form.student_name} onChange={upd("student_name")} placeholder="As per birth certificate" data-testid="adm-student-name" /></PubField></div>
      <PubField label="Gender *"><select className="pub-input" value={form.gender} onChange={upd("gender")} data-testid="adm-gender"><option>Male</option><option>Female</option></select></PubField>
      <PubField label="Date of Birth *"><DateInput className="pub-input" value={form.dob} onChange={upd("dob")} data-testid="adm-dob" /></PubField>
      <PubField label="Class Applying For *">
        <select className="pub-input" value={form.class_applying} onChange={upd("class_applying")} data-testid="adm-class">
          <option value="">Select class</option>
          {classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </PubField>
      <PubField label="Aadhaar No (optional)"><input className="pub-input" value={form.aadhaar} onChange={upd("aadhaar")} maxLength={12} data-testid="adm-aadhaar" /></PubField>
      <div className="sm:col-span-2"><PubField label="Previous School (if any)"><input className="pub-input" value={form.previous_school} onChange={upd("previous_school")} data-testid="adm-prev-school" /></PubField></div>
    </div>
  );
}

export function ParentStep({ form, upd, otp, setOtp }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/otp/send`, { phone: form.parent_phone });
      setOtp({ ...otp, sent: true, mock: data.mock_otp, code: "" });
      toast.success(`OTP sent to ${form.parent_phone}`, { description: data.mock ? `MOCK SMS — your OTP is ${data.mock_otp}` : undefined, duration: 8000 });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/otp/verify`, { phone: form.parent_phone, otp: otp.code });
      setOtp({ ...otp, verified: true, token: data.verify_token });
      toast.success("Mobile number verified");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <PubField label="Parent / Guardian Name *"><input className="pub-input" value={form.parent_name} onChange={upd("parent_name")} data-testid="adm-parent-name" /></PubField>
      <PubField label="Occupation"><input className="pub-input" value={form.occupation} onChange={upd("occupation")} data-testid="adm-occupation" /></PubField>
      <PubField label="Email (optional)"><input type="email" className="pub-input" value={form.parent_email} onChange={upd("parent_email")} data-testid="adm-email" /></PubField>
      <div className="sm:col-span-2"><PubField label="Residential Address *"><textarea rows={2} className="pub-input" value={form.address} onChange={upd("address")} data-testid="adm-address" /></PubField></div>

      <div className="sm:col-span-2 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4" data-testid="otp-box">
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800"><ShieldCheck size={16} /> Verify parent mobile number</div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <input className="pub-input" placeholder="10-digit mobile" value={form.parent_phone} onChange={(e) => { upd("parent_phone")(e); setOtp({ sent: false, verified: false, code: "", token: "" }); }} maxLength={10} disabled={otp.verified} data-testid="adm-phone" />
          {!otp.verified && <button className="pub-btn shrink-0" onClick={send} disabled={busy || form.parent_phone.length < 10} data-testid="send-otp-btn">{busy && !otp.sent ? <Loader2 size={15} className="animate-spin" /> : null} {otp.sent ? "Resend OTP" : "Send OTP"}</button>}
        </div>
        {otp.sent && !otp.verified && (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input className="pub-input font-mono tracking-[0.4em]" placeholder="••••••" maxLength={6} value={otp.code} onChange={(e) => setOtp({ ...otp, code: e.target.value.replace(/\D/g, "") })} data-testid="adm-otp" />
            <button className="pub-btn shrink-0 !bg-indigo-600" onClick={verify} disabled={busy || otp.code.length !== 6} data-testid="verify-otp-btn">Verify</button>
          </div>
        )}
        {otp.verified && <p className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid="otp-verified"><FileCheck2 size={15} /> Verified ✓</p>}
        {otp.sent && !otp.verified && otp.mock && <p className="mt-2 text-[11px] text-slate-500">SMS gateway not connected yet (MOCK) — OTP: <b className="font-mono">{otp.mock}</b></p>}
      </div>
    </div>
  );
}

const DOCS = [
  { key: "photo", label: "Student Passport Photo", accept: "image/*", hint: "JPG/PNG, max 5 MB" },
  { key: "birth_cert", label: "Birth Certificate", accept: "image/*,application/pdf", hint: "PDF or image" },
  { key: "tc", label: "Transfer Certificate / Previous Marks", accept: "image/*,application/pdf", hint: "PDF or image" },
  { key: "aadhaar_doc", label: "Aadhaar Card", accept: "image/*,application/pdf", hint: "PDF or image" },
];

export function DocsStep({ docs, setDocs, required = ["photo", "birth_cert"] }) {
  const [prog, setProg] = useState({});
  const pick = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await uploadPublic(file, key, (p) => setProg((x) => ({ ...x, [key]: p })));
      setDocs({ ...docs, [key]: r });
      toast.success(`${file.name} uploaded`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setProg((x) => ({ ...x, [key]: 0 })); }
  };
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {DOCS.map((d) => {
        const f = docs[d.key];
        return (
          <div key={d.key} className="rounded-2xl border border-dashed border-slate-300 bg-white p-4" data-testid={`doc-${d.key}`}>
            <p className="text-xs font-semibold text-slate-600">{d.label} {required.includes(d.key) ? <span className="text-rose-500">*</span> : <span className="text-slate-400">(optional)</span>}</p>
            {f ? (
              <div className="mt-3 flex items-center gap-3">
                {f.filename.match(/\.(jpe?g|png|webp)$/i) ? <img src={fileUrl(f.url)} alt="" className="h-14 w-14 rounded-lg object-cover ring-1 ring-slate-200" /> : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-rose-50 text-rose-500"><FileCheck2 size={22} /></div>}
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{f.filename}</p><p className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</p></div>
                <button onClick={() => setDocs({ ...docs, [d.key]: undefined })} className="text-slate-400 hover:text-rose-500" data-testid={`doc-remove-${d.key}`}><X size={16} /></button>
              </div>
            ) : (
              <label className="mt-3 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl bg-slate-50 py-5 text-slate-500 transition hover:bg-indigo-50 hover:text-indigo-600">
                {prog[d.key] ? <><Loader2 size={20} className="animate-spin" /><span className="text-xs">{prog[d.key]}%</span></> : <><UploadCloud size={22} /><span className="text-xs font-medium">Click to upload</span><span className="text-[11px] text-slate-400">{d.hint}</span></>}
                <input type="file" className="hidden" accept={d.accept} onChange={pick(d.key)} data-testid={`doc-input-${d.key}`} />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}
